import {ema35Clock} from './ema35-clock.js';
export const BASELINE_POLICY='baseline';
export const LAST_CANDLE_POLICY='last-candle-aligned';
export const SELECTIVE_POLICY='selective-confluence';
export const EMA_CLOCK_POLICY='selective-ema35-clock';
export const DEFAULT_SIGNAL_POLICY=EMA_CLOCK_POLICY;
export const SELECTIVE_GATE_LABELS={baseline_filters:'Filtros da janela de 15 velas',finite_indicators:'Indicadores válidos',
  trend_alignment:'Tendência de preço na direção da entrada',
  ma35_history_matches_signal:'Histórico EMA35 no mesmo corte do sinal',
  ma35_clock_alignment:'EMA35 no ângulo do relógio exigido',
  rsi_direction:'RSI na direção da entrada',di_direction:'DI na direção e diferença mínima de 10',
  adx_strength:'ADX de pelo menos 25',last_two_aligned:'Duas últimas velas na direção',
  close_progression:'Fechamentos avançando na direção',recent_di_alignment:'Movimento direcional da última vela confirmado'};
export const isValidSignalPolicy=value=>[BASELINE_POLICY,LAST_CANDLE_POLICY,SELECTIVE_POLICY,EMA_CLOCK_POLICY].includes(value);
export const isValidLossLimit=value=>Number.isInteger(value)&&value>=0&&value<=20;
export function priceTrend(candles){
  if(!Array.isArray(candles)||candles.length!==15||candles.some(c=>!Number.isFinite(c.close)))return {direction:null,slope:null,net_delta:null};
  const mean=candles.reduce((sum,c)=>sum+c.close,0)/15;
  const slope=candles.reduce((sum,c,i)=>sum+(i-7)*(c.close-mean),0)/280;
  const net_delta=candles.at(-1).close-candles[0].close;
  return {direction:slope>0&&net_delta>0?'CALL':slope<0&&net_delta<0?'PUT':null,slope,net_delta};
}
export function selectiveGates(candidate){
  const raw=candidate?.raw_indicators??{},call=candidate?.direcao==='CALL',put=candidate?.direcao==='PUT';
  const candles=Array.isArray(candidate?.source_candles)?candidate.source_candles:[],last=candles.at(-1),previous=candles.at(-2);
  const valid=Array.isArray(candles)&&candles.length===15&&[last,previous].every(c=>c&&[c.open,c.close,c.high,c.low].every(Number.isFinite)&&c.high>=Math.max(c.open,c.close)&&c.low<=Math.min(c.open,c.close));
  const up=valid?last.high-previous.high:null,down=valid?previous.low-last.low:null;
  const required=['direction_defined','finite_indicators','di_alignment','rsi50_alignment','raw_di_gap_at_least10','score_at_least65'];
  const finite=['rsi','adx','plus_di','minus_di'].every(k=>Number.isFinite(raw[k])&&raw[k]>=0&&raw[k]<=100);
  return {
    baseline_filters:candidate?.approved===true&&required.every(k=>candidate.gates?.[k]===true),
    finite_indicators:finite,
    trend_alignment:(call||put)&&priceTrend(candles).direction===candidate.direcao,
    rsi_direction:finite&&(call?raw.rsi>50:put&&raw.rsi<50),
    di_direction:finite&&Math.abs(raw.plus_di-raw.minus_di)>=10&&(call?raw.plus_di>raw.minus_di:put&&raw.minus_di>raw.plus_di),
    adx_strength:finite&&raw.adx>=25,
    last_two_aligned:valid&&(call?[last,previous].every(c=>c.close>c.open):put&&[last,previous].every(c=>c.close<c.open)),
    close_progression:valid&&(call?last.close>previous.close:put&&last.close<previous.close),
    recent_di_alignment:valid&&(call?up>0&&up>down:put&&down>0&&down>up),
  };
}
export function lastCandleAligned(candidate){
  const candles=candidate?.source_candles;
  if(!Array.isArray(candles)||candles.length!==15)return false;
  const last=candles.at(-1);
  if(!Number.isFinite(last?.open)||!Number.isFinite(last?.close))return false;
  return candidate.direcao==='CALL'?last.close>last.open:candidate.direcao==='PUT'?last.close<last.open:false;
}
export function applySignalPolicy(result,policy=BASELINE_POLICY){
  if(!isValidSignalPolicy(policy))throw Error('Perfil de sinal inválido.');
  if(result.status!=='selected')return {...result,signal_policy:policy};
  if(policy===BASELINE_POLICY)return {...result,signal_policy:policy,winner:{...result.winner,signal_policy:policy}};
  if(policy===SELECTIVE_POLICY||policy===EMA_CLOCK_POLICY){
    const gates=selectiveGates(result.winner);
    const ma35=policy===EMA_CLOCK_POLICY?ema35Clock(result.winner?.ma_source_candles,{schedule:result.winner?.ma_schedule}):null;
    if(ma35){
      const recent=Array.isArray(result.winner?.ma_source_candles)?result.winner.ma_source_candles.slice(-15):[],window=result.winner?.source_candles;
      gates.ma35_history_matches_signal=ma35.valid&&Array.isArray(window)&&window.length===15&&
        recent.every((c,i)=>['from','open','close','high','low'].every(k=>c[k]===window[i]?.[k]));
      gates.ma35_clock_alignment=ma35.valid&&ma35.direction===result.winner?.direcao;
    }
    const failed=Object.keys(gates).filter(k=>!gates[k]);
    if(failed.length)return {...result,status:'filtered',winner:null,signal_policy:policy,quality_gates:gates,
      ...(ma35?{ma35}:{}),
      filter_reason:`Confluência não aprovada: ${failed.map(k=>SELECTIVE_GATE_LABELS[k]).join('; ')}. Nenhuma entrada enviada.`};
    return {...result,signal_policy:policy,winner:{...result.winner,signal_policy:policy,
      rule_version:ma35?'experimental-independent-m1-v5-ema35-clock':'experimental-independent-m1-v4-confluence',
      ...(ma35?{ma35}:{}),quality_gates:gates,trend:priceTrend(result.winner.source_candles)}};
  }
  if(!lastCandleAligned(result.winner))return {...result,status:'filtered',winner:null,signal_policy:policy,
    filter_reason:'A última vela fechada do sinal selecionado está contra a direção ou sem variação. Nenhum outro ativo foi escolhido automaticamente.'};
  return {...result,signal_policy:policy,winner:{...result.winner,signal_policy:policy,
    rule_version:'experimental-independent-m1-v3-last-candle',quality_gates:{last_candle_aligned:true}}};
}
